import { useCallback, type FC } from "react";
import { useForm, type DeepPartial } from "react-hook-form";
import styled from "styled-components";
import { cloneDeep } from "lodash";

import { useGetChangedFieldsOnSubmitHandlerDecorator, useGetHandleSubmit } from "@ds/hooks/redux-forms-hooks";
import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useCurrentProjectFeatureEnabled } from "@ds/hooks/use-feature";

import { FEATURE_KEYS } from "@ds/modules/features/@types";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";
import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { selectAggregatedPlaylistAssetByIds } from "../../redux/selectors/common-selectors";
import { playlistAssetsActions } from "../../redux/slice";
import { PlaylistAssetDateControl } from "../controls/playlist-asset-date-control";
import { PlaylistAssetDurationControl } from "../controls/playlist-asset-duration-control";

const ContentWrapper = styled.div`
  min-height: 200px;
  width: 500px;
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: right;
`;

const serializeDateString = (dateString: string) => {
  return new Date(dateString).toISOString();
};

const serializeChangedFields = (changedFields: DeepPartial<PlaylistAsset>) => {
  const normalizedChangedFields = cloneDeep(changedFields);
  if (changedFields.start_at) {
    normalizedChangedFields.start_at = serializeDateString(changedFields.start_at as string);
  }
  if (changedFields.end_at) {
    normalizedChangedFields.end_at = serializeDateString(changedFields.end_at as string);
  }
  return normalizedChangedFields;
};

export const UpdatePlaylistAssetModal: FC<{
  initialValue: PlaylistAsset;
  onClose: () => void;
}> = ({ initialValue, onClose }) => {
  const aggregatedPlaylistAssetIds = useGetAggregatedEntityIds(initialValue);
  const aggregatedPlaylistAsset = useRootSelector(state =>
    selectAggregatedPlaylistAssetByIds(state, aggregatedPlaylistAssetIds),
  ) as AggregatedEntity<PlaylistAsset>;
  const {
    control,
    trigger,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<PlaylistAsset>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initialValue,
  });

  const isPlaylistItemStartEndEnabled = useCurrentProjectFeatureEnabled(FEATURE_KEYS.PLAYLIST_ITEM_START_END);
  const isPlaylistItemDurationEnabled = useCurrentProjectFeatureEnabled(FEATURE_KEYS.PLAYLIST_ITEM_DURATION);

  const dispatch = useRootDispatch();
  const handleSubmit = useGetHandleSubmit({
    getValues,
    dirtyFields,
    trigger,
    errors,
  });

  const onSubmitHandler = useCallback(
    async (changedFields: DeepPartial<PlaylistAsset>) => {
      const normalizedChangedFields = serializeChangedFields(changedFields);
      dispatch(playlistAssetsActions.updatePlaylistAssets(aggregatedPlaylistAssetIds, normalizedChangedFields));
      onClose();
    },
    [dispatch, aggregatedPlaylistAssetIds, onClose],
  );

  const onSubmitHandlerDecorator = useGetChangedFieldsOnSubmitHandlerDecorator({
    dirtyFields,
    onSubmit: onSubmitHandler,
  });

  const renderFooter = useCallback(
    () => (
      <FooterWrapper>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          onClick={onClose}
        />

        <Button
          label="Update"
          ariaLabel="update button"
          buttonType={ButtonType.Raised}
          icon={IconType.Save}
          iconPosition="right"
          isDisabled={Object.keys(dirtyFields).length === 0}
          onClick={handleSubmit(onSubmitHandlerDecorator)}
        />
      </FooterWrapper>
    ),
    [onClose, handleSubmit, onSubmitHandlerDecorator, dirtyFields],
  );

  return (
    <Dialog modal visible header="Update playlist asset(s)" footer={renderFooter} onHide={onClose}>
      <ContentWrapper>
        {isPlaylistItemDurationEnabled && <PlaylistAssetDurationControl control={control} />}
        {isPlaylistItemStartEndEnabled && (
          <>
            <PlaylistAssetDateControl
              control={control}
              name="start_at"
              placeholder={
                !dirtyFields.start_at && aggregatedPlaylistAsset?.metadata?.aggregatedFields?.start_at
                  ? "Multiple values"
                  : undefined
              }
            />
            <PlaylistAssetDateControl
              control={control}
              name="end_at"
              placeholder={
                !dirtyFields.end_at && aggregatedPlaylistAsset?.metadata?.aggregatedFields?.end_at
                  ? "Multiple values"
                  : undefined
              }
            />
          </>
        )}
      </ContentWrapper>
    </Dialog>
  );
};
